import {SpaAsyncConfig, SpaGlobalConfig} from '../../../generated/core';
import {
    FeatureHub,
    FeatureServiceProviderDefinition,
    SharedFeatureService
} from '@feature-hub/core';
import {Logger} from '../../context/logger/Logger';
import {
    isV12,
    InspectFaServiceV1,
    FeatureServiceRegistryInfo
} from '@volkswagen-onehub/inspect-fa-service';
import {LayerHandleV2} from '@volkswagen-onehub/layer-manager';

import {createGetLoggerRef} from './createGetLoggerRef';
import {getMocksServerConfig} from './getMocksServerConfig';
import {createDependencies} from './createDependencies';
import {createServices} from './createServices';
import {createBasePath} from './createBasePath';
import {createIntegratorLoggerRefs} from './createIntegratorLoggerRefs';

export const externals = [
    'react',
    'react-dom',
    '@feature-hub/react',
    'styled-components'
];

export function initInspectFaService(
    featureHub: FeatureHub,
    config: SpaGlobalConfig,
    asyncConfig: SpaAsyncConfig,
    logger: Logger,
    moduleDefinitionsMap: {
        moduleDef: FeatureServiceProviderDefinition<SharedFeatureService>;
        bundle: string;
    }[]
) {
    try {
        const inspectFaModel = asyncConfig.inspectFaModel;

        // get service
        const inspectFaService = featureHub.featureServices[
            'inspect-fa-service'
        ] as InspectFaServiceV1 | undefined;

        if (!inspectFaService) {
            return;
        }
        const isV12Service = isV12(inspectFaService);

        // listen to key shortcut
        let layerHandle: LayerHandleV2<unknown> | undefined;
        document.addEventListener(
            'keyup',
            e => {
                if (!layerHandle || layerHandle.isClosed())
                    if (
                        e.ctrlKey &&
                        e.altKey &&
                        e.shiftKey &&
                        e.code === 'KeyI'
                    ) {
                        layerHandle = inspectFaService.openInspectorLayer();
                    }
            },
            false
        );

        // init with CMS data
        const basePath = createBasePath(config);

        // data for "Feature Serivces" tab
        const featureServiceRegistryInfo: FeatureServiceRegistryInfo = {
            get featureServices() {
                return createServices(featureHub, moduleDefinitionsMap);
            }
        };

        // data for "Dependencies" tab
        const dependencies = createDependencies(config, asyncConfig);

        // data for "Mocky" tab
        const mocksServerConfig = getMocksServerConfig(inspectFaModel);

        // data for "Integrator Views" tab
        const views = async () => {
            return (
                await import(
                    /*  webpackChunkName: "inspectViews"  */ './getViews'
                )
            ).getViews(config, asyncConfig, !isV12Service);
        };

        inspectFaService.initialize({
            featureServiceBundleJsonUrl:
                asyncConfig.featureHubConfiguration.clientBundleJsonUrl,
            basePath,
            testScenarios: inspectFaModel?.testScenarios,
            mocksServerConfig,
            getLoggerRef: createGetLoggerRef(logger),
            integratorLoggers: createIntegratorLoggerRefs(logger),
            featureServiceRegistryInfo,
            dependencies,
            views
        });

        if (isV12Service) {
            inspectFaService.addCacheMonitoring({
                name: 'HTML',
                status: {
                    date: new Date(config.generationTime ?? 0).toISOString()
                },
                type: 'AEM',
                url: window.location.href,
                ssr: true,
                children: []
            });
            inspectFaService.addCacheMonitoring({
                name: 'Global Config',
                status: {
                    date: new Date(
                        asyncConfig.generationTime ?? 0
                    ).toISOString()
                },
                url: config.asyncConfigUrl + window.location.search,
                ssr: false,
                type: 'AEM',
                children: []
            });
        }
    } catch (e) {
        logger.general.warn('failed to initialize inspect fa service', e);
    }
}
